import React, { ChangeEvent, FC, useState } from 'react';
import { Input, InputContainer } from '@mercell/input-react';
import Button from '@mercell/button-react';
import { ProgressSpinner } from '../../components/ProgressSpinner';
import { getLogs } from '../../hooks/administrativeHooks/useFetchLogs';
import { useAppSelector } from '../../redux/hooks';
import { LogsModelSearch } from '../../types/generated/logsModelSearch';
import { LogsModel } from '../../types/generated/logsModel';
import { DatePicker } from '@mercell/date-picker-react';
import { useTranslation } from 'react-i18next';

const CloudWatchLogsTab: FC = () => {
    const [searchString, setSearchString] = useState<string>();
    const [extendedSeconds, setExtendedSeconds] = useState<string>();
    const [logs, setLogs] = useState<LogsModel[] | undefined>([]);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [startDateTime, setStartDateTime] = React.useState<Date | null>();
    const [endDateTime, setEndDateTime] = React.useState<Date | null>();

    const reduxDateFormat = useAppSelector((state) => state.store.dateFormat);
    const { t } = useTranslation(['form-content.logs']);

    const onSearch = async () => {
        setLogs(undefined);
        setSearchError(null);

        const payload: LogsModelSearch = {
            queryString: searchString,
            startTime: startDateTime ?? undefined,
            endTime: endDateTime ?? undefined,
            extendedLogSeconds: Number(extendedSeconds ?? '0'),
        };

        try {
            const resData = await getLogs(payload);
            setLogs(resData);
        } catch {
            setSearchError(
                t('form-content.logs:cloud-watch.searchFailed')
            );
            setLogs([]);
        }
    };

    if (!logs) {
        return (
            <div className="col-span-full h-full flex items-center justify-center">
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between">
                <div className="mb-10">
                    <p>
                        {t('form-content.logs:cloud-watch.stringToSearch')}
                    </p>
                    <InputContainer>
                        <Input
                            placeholder={t(
                                'form-content.logs:cloud-watch.searchPlaceholder'
                            )}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setSearchString(e.currentTarget.value)
                            }
                        />
                    </InputContainer>
                    <p>
                        {t(
                            'form-content.logs:cloud-watch.numberOfSeconds'
                        )}
                    </p>
                    <InputContainer>
                        <Input
                            placeholder={t(
                                'form-content.logs:cloud-watch.extendedLogSecondsPlaceholder'
                            )}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setExtendedSeconds(e.currentTarget.value)
                            }
                        />
                    </InputContainer>

                    <p>
                        {t(
                            'form-content.logs:cloud-watch.logStartDateAndTime'
                        )}
                    </p>
                    <DatePicker
                        onChange={(date) => setStartDateTime(date)}
                        selected={startDateTime}
                        showTimeSelect
                        timeIntervals={5}
                        dateFormat={reduxDateFormat.DateTime}
                    />
                    <p>
                        {t(
                            'form-content.logs:cloud-watch.logEndDateAndTime'
                        )}
                    </p>
                    <DatePicker
                        onChange={(date) => setEndDateTime(date)}
                        selected={endDateTime}
                        showTimeSelect
                        timeIntervals={5}
                        dateFormat={reduxDateFormat.DateTime}
                    />
                    <Button scheme="primary" onClick={onSearch}>
                        {t('form-content.logs:cloud-watch.search')}
                    </Button>
                    {searchError && (
                        <p className="text-red-600 text-sm mt-2">
                            {searchError}
                        </p>
                    )}
                </div>
            </div>
            <pre>{JSON.stringify(logs, undefined, 2)}</pre>
        </div>
    );
};

export default CloudWatchLogsTab;
